/**
 * Schedule oversight — slots and bookings across therapists (AD-04, D-16).
 *
 * Used by both admin and clinical supervisors; what each sees is decided by
 * the server, not here. A clinical supervisor gets two independent tests:
 *
 *   schedule visibility — therapist shares one of their service groups, or is
 *                         supervised by them
 *   client identity     — therapist is supervised by them AND the supervisor
 *                         is assigned to that service group
 *
 * Bookings that fail the second test arrive with `client: null` and
 * `clientRedacted: true`. That is the intended state, not missing data: seeing
 * that a therapist is committed is what stops a supervisor over-allocating
 * them, and it needs no name attached. This component must never try to
 * resolve a redacted client from elsewhere.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, MenuItem, IconButton, Button
} from '@mui/material';
import { Lock as LockIcon, Event as EventIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { getSchedule } from '../../services/availabilityApi';

const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

const STATUS_COLOR = { open: 'success', booked: 'primary', cancelled: 'default' };

const isoDay = (d) => d.toISOString().slice(0, 10);

export default function ScheduleOversight({ token }) {
  const [data, setData] = useState({ slots: [], scope: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [slotType, setSlotType] = useState('all');
  const [status, setStatus] = useState('all');
  const [therapistFilter, setTherapistFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState(isoDay(new Date()));
  const [to, setTo] = useState(isoDay(new Date(Date.now() + 42 * 864e5)));

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getSchedule({
        from: new Date(from).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
        slotType: slotType === 'all' ? undefined : slotType
      }, token);
      setData({ slots: res?.slots || [], scope: res?.scope || null });
    } catch (err) {
      console.error('Error loading schedule:', err);
      setError(err.response?.data?.msg || 'Could not load the schedule');
    } finally {
      setLoading(false);
    }
  }, [token, from, to, slotType]);

  useEffect(() => { load(); }, [load]);

  const therapists = useMemo(() => {
    const map = new Map();
    for (const s of data.slots || []) {
      const id = String(s.therapist?._id || '');
      if (id && !map.has(id)) map.set(id, s.therapist.displayName || 'Unknown');
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data.slots]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.slots || []).filter(s => {
      if (status !== 'all' && s.status !== status) return false;
      if (therapistFilter !== 'all' && String(s.therapist?._id) !== therapistFilter) return false;
      if (!q) return true;
      // Search covers therapist, client, and service group. A redacted booking
      // has no client text to match, which is correct — it must not be
      // findable by a client name the viewer is not entitled to see.
      const hay = [
        s.therapist?.displayName,
        s.client?.name,
        s.client?.email,
        ...(s.serviceGroups || []).map(g => g.name)
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [data.slots, status, therapistFilter, search]);

  const summary = useMemo(() => {
    const s = filtered;
    const byTherapist = new Map();
    for (const slot of s) {
      const key = slot.therapist?.displayName || 'Unknown';
      if (!byTherapist.has(key)) byTherapist.set(key, { open: 0, booked: 0 });
      if (slot.status === 'open') byTherapist.get(key).open += 1;
      if (slot.status === 'booked') byTherapist.get(key).booked += 1;
    }
    return {
      total: s.length,
      open: s.filter(x => x.status === 'open').length,
      booked: s.filter(x => x.status === 'booked').length,
      redacted: s.filter(x => x.clientRedacted).length,
      intakeOpen: s.filter(x => x.status === 'open' && x.slotType === 'intake').length,
      exitOpen: s.filter(x => x.status === 'open' && x.slotType === 'exit').length,
      byTherapist: [...byTherapist.entries()]
    };
  }, [filtered]);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <EventIcon color="primary" />
          <Typography variant="h6">Therapist schedules</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Published slots and bookings, so you can see where capacity is and where it is running out.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {data.scope && !data.scope.isAdmin && (
          <Alert severity="info" icon={<LockIcon fontSize="inherit" />} sx={{ mb: 2 }}>
            You can see availability for therapists in your service groups and for those you supervise.
            Client names are shown only for therapists you supervise within your own service groups.
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
          <TextField
            label="From" type="date" size="small" value={from}
            onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To" type="date" size="small" value={to}
            onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }}
          />
          <ToggleButtonGroup exclusive size="small" value={slotType} onChange={(_, v) => v && setSlotType(v)}>
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="intake">Intake</ToggleButton>
            <ToggleButton value="exit">Exit</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Client, therapist or service group"
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />,
              endAdornment: search ? (
                <IconButton size="small" onClick={() => setSearch('')} aria-label="clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null
            }}
          />
          <TextField
            select size="small" label="Therapist" value={therapistFilter}
            onChange={(e) => setTherapistFilter(e.target.value)} sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All therapists</MenuItem>
            {therapists.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
          </TextField>
          <ToggleButtonGroup exclusive size="small" value={status} onChange={(_, v) => v && setStatus(v)}>
            <ToggleButton value="all">Any</ToggleButton>
            <ToggleButton value="open">Free</ToggleButton>
            <ToggleButton value="booked">Booked</ToggleButton>
            <ToggleButton value="cancelled">Withdrawn</ToggleButton>
          </ToggleButtonGroup>
          {(search || status !== 'all' || therapistFilter !== 'all') && (
            <Button size="small" onClick={() => { setSearch(''); setStatus('all'); setTherapistFilter('all'); }}>
              Clear
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${filtered.length} of ${data.slots.length} shown`} variant="outlined" />
          <Chip size="small" label={`${summary.booked} booked`} color="primary" variant="outlined" />
          <Chip size="small" label={`${summary.intakeOpen} intake open`} color={summary.intakeOpen ? 'success' : 'warning'} variant="outlined" />
          {/* Exit capacity is the one that fails silently — a cohort reaches
              week 4 with nowhere to book its closing calls (risk R-08). */}
          <Tooltip title="Exit slots are needed when the first clients reach the end of the programme. If this is zero, closing appointments cannot be booked.">
            <Chip size="small" label={`${summary.exitOpen} exit open`} color={summary.exitOpen ? 'success' : 'warning'} variant="outlined" />
          </Tooltip>
          {summary.redacted > 0 && (
            <Chip size="small" icon={<LockIcon />} label={`${summary.redacted} outside your remit`} variant="outlined" />
          )}
        </Stack>

        {loading && <CircularProgress size={22} />}

        {!loading && !filtered.length && (
          <Alert severity="info">
            {data.slots.length
              ? 'No slots match these filters.'
              : 'No slots published in this window.'}
          </Alert>
        )}

        {!loading && !!filtered.length && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Therapist</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Service group</TableCell>
                  <TableCell>Client</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(s => (
                  <TableRow key={s._id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {fmtDay(s.startsAt)} {fmtTime(s.startsAt)}
                    </TableCell>
                    <TableCell>{s.therapist?.displayName || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={s.slotType} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={s.status} color={STATUS_COLOR[s.status] || 'default'} />
                    </TableCell>
                    <TableCell>
                      {(s.serviceGroups || []).map(g => (
                        <Chip key={g._id} size="small" variant="outlined" label={g.name} sx={{ mr: 0.5 }} />
                      ))}
                    </TableCell>
                    <TableCell>
                      {s.client ? (
                        s.client.name || s.client.email
                      ) : s.clientRedacted ? (
                        <Tooltip title="Booked. You do not have visibility of clients for this therapist.">
                          <Chip size="small" icon={<LockIcon />} label="Booked" variant="outlined" />
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {!loading && summary.byTherapist.length > 1 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Capacity by therapist</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {summary.byTherapist.map(([name, c]) => (
                <Chip key={name} size="small" variant="outlined" label={`${name}: ${c.open} open / ${c.booked} booked`} />
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
